import { useState, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import {
  Menu,
  X,
  Home,
  Info,
  CheckCircle2,
  BookOpen,
  Briefcase,
  PhoneCall,
  Handshake,
} from "lucide-react";
import { Button } from "../ui/button";
import { SITE, NAV_LINKS } from "../../constants";
import MobileNav from "./MobileNav";

const NAV_ICONS = {
  "/": Home,
  "/about": Info,
  "/why-choose-us": CheckCircle2,
  "/blog": BookOpen,
  "/careers": Briefcase,
  "/contact": PhoneCall,
};

export default function Header({ onPartnerClick }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 16);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="fixed top-3 left-0 right-0 z-50 mx-auto w-[98%] max-w-[1580px] px-2 sm:px-4 transition-all duration-300 ease-out">
      <header
        className={`relative flex items-center justify-between rounded-full border transition-all duration-300 ease-out ${
          scrolled
            ? "border-slate-200 bg-white/95 backdrop-blur-md px-4 py-2.5 shadow-[0_10px_30px_rgba(0,0,0,0.08)] sm:px-6"
            : "border-slate-200/80 bg-white/90 backdrop-blur-md px-5 py-3 shadow-[0_4px_20px_rgba(0,0,0,0.04)] sm:px-7"
        }`}
      >
        {/* Brand Logo & Heading Image */}
        <Link
          to="/"
          className="group flex items-center gap-2.5 sm:gap-3.5 transition-transform duration-300 hover:scale-[1.02]"
          data-testid="brand-link"
        >
          <img
            src="/crescet-favicon.png"
            alt={SITE.brand}
            draggable={false}
            className="h-10 w-10 sm:h-11 sm:w-11 select-none object-contain mix-blend-multiply transition-transform duration-300 group-hover:scale-105 shrink-0"
          />
          <img
            src="/crescet-heading.png"
            alt={SITE.brand}
            draggable={false}
            className="h-9 sm:h-10 md:h-11 w-auto select-none object-contain mix-blend-multiply transition-transform duration-300 group-hover:scale-[1.01]"
          />
        </Link>

        {/* Desktop Navigation Pill Links */}
        <nav className="hidden items-center gap-1 rounded-full bg-black/[0.03] p-1 text-sm font-medium text-gray-700 ring-1 ring-black/[0.04] lg:flex">
          {NAV_LINKS.map((l) => {
            const IconComponent = NAV_ICONS[l.to];
            return (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                className={({ isActive }) =>
                  `relative flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs xl:text-sm transition-all duration-300 hover:text-[#0B5D4B] hover:bg-white ${
                    isActive
                      ? "bg-white font-semibold text-[#0B5D4B] shadow-sm ring-1 ring-black/5"
                      : "text-gray-600"
                  }`
                }
                data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {IconComponent && (
                  <IconComponent size={14} className="shrink-0 opacity-70" />
                )}
                <span>{l.label}</span>
              </NavLink>
            );
          })}
        </nav>

        {/* Action Buttons & Mobile Toggle */}
        <div className="flex items-center gap-2 sm:gap-2.5">
          <Button
            asChild
            className="hidden rounded-full bg-[#0B5D4B] px-5 py-2 text-xs font-semibold uppercase tracking-wider text-white shadow-md shadow-[#0B5D4B]/20 transition-all duration-300 hover:bg-[#063B30] hover:shadow-lg hover:shadow-[#0B5D4B]/30 hover:scale-[1.03] active:scale-95 sm:text-sm sm:normal-case sm:tracking-normal md:inline-flex cursor-pointer"
            data-testid="header-partner-btn"
          >
            <Link to="/contact" className="inline-flex items-center gap-1.5">
              <Handshake size={16} className="shrink-0" />
              <span>Partner With Us</span>
            </Link>
          </Button>
          <button
            onClick={() => setMobileOpen((v) => !v)}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-black/10 bg-white/80 p-2 text-gray-700 shadow-sm backdrop-blur transition-all duration-300 hover:bg-white hover:text-[#0B5D4B] hover:shadow-md lg:hidden"
            aria-label="Menu"
            data-testid="mobile-menu-btn"
          >
            {mobileOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </header>

      {/* Floating Island Mobile Navigation */}
      <MobileNav
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        onPartnerClick={onPartnerClick}
      />
    </div>
  );
}
